import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import CustomerBillingReceipt from "./CustomerBillingReceipt";
import Billpopup from "./Billpopup";
import { customFetch } from '../apiClient';

// ✅ DYNAMIC STYLES FOR REACT SELECT (COMPACT)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};
const ReportBillSummary = () => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedSearch, setSelectedSearch] = useState(null); 
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  const [billDataList, setBillDataList] = useState([]);
  const [editBillNo, setEditBillNo] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  const token = sessionStorage.getItem("token");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // ✅ NAYA CODE
useEffect(() => {
  const hospitalId = sessionStorage.getItem("hospitalId");
  if (hospitalId) {
    customFetch(`/Company/${hospitalId}`)
      .then((res) => res.json())
      .then(setCompanyDetails)
      .catch(console.error);
  }
}, []);

  // ✅ LOAD OPTIONS FOR ASYNC SELECT
  const loadSearchOptions = async (inputValue) => {
    if (!inputValue) return [];
   try {
    const res = await customFetch(`/Billing/search-advanced?customerName=${inputValue}`);
      if (!res.ok) return [];
      const data = await res.json();
      const list = data.data || data || [];
      
      return list.map(item => ({
        label: `${item.customer?.custName} (${item.customer?.mobileNo})`,
        value: item.customer?.custName, 
        mobile: item.customer?.mobileNo
      }));
    } catch (err) {
      return [];
    }
  };

  // ✅ Handle Search Selection
  const handleSearchSelect = (selected) => {
    setSelectedSearch(selected);
    handleSearch(1, rowsPerPage || 10, selected);
  };

  const handleSearch = async (page = currentPage, rows = rowsPerPage || 10, searchOpt = selectedSearch) => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", new Date(fromDate).toISOString());
      if (toDate) params.append("toDate", new Date(toDate).toISOString());
      params.append("page", page);
      params.append("pageSize", rows);

      if (searchOpt) {
        if (/^\d+$/.test(searchOpt.value)) {
             params.append("mobileNo", searchOpt.value);
        } else {
             params.append("customerName", searchOpt.value);
        }
      }

     const res = await customFetch(`/Billing/search-advanced?${params.toString()}`);

      if (!res.ok) {
        setBillDataList([]); setTotalRecords(0); return;
      }

      const data = await res.json();
      setBillDataList(data.data || data || []);
      setTotalRecords(data.totalRecords || data.length || 0);
      setCurrentPage(page);
    } catch (e) {
      console.error("Search failed", e);
      setBillDataList([]); setTotalRecords(0);
    }
  };

  const handleDeleteSummary = async (billNo) => {
    if (!window.confirm("Delete this summary?")) return;
   try {
    const res = await customFetch(`/BillSummary/${billNo}`, {
      method: "DELETE"
    });
      if (!res.ok) throw new Error("Failed to delete summary");
      handleSearch(currentPage, rowsPerPage || 10);
    } catch (e) { alert(e.message); }
  };

  const handleEditIconClick = (e, billNo) => {
    e.stopPropagation();
    const popupWidth = Math.min(900, window.innerWidth * 0.9);
    const popupHeight = Math.min(700, window.innerHeight * 0.9);
    let centerX = (window.innerWidth - popupWidth) / 2 + window.scrollX;
    let centerY = (window.innerHeight - popupHeight) / 2 + window.scrollY;
    
    // Boundary Checks
    centerX = Math.max(10, Math.min(centerX, window.innerWidth - popupWidth - 10 + window.scrollX));
    centerY = Math.max(20, Math.min(centerY, window.innerHeight - popupHeight - 20 + window.scrollY));
    
    setPopupPosition({ x: centerX, y: centerY });
    setEditBillNo(billNo);
  };

  const handlePreviewClick = async (billNo) => {
    try {
    const res = await customFetch(`/Billing/search?billNo=${encodeURIComponent(billNo)}`);
      if (!res.ok) throw new Error("Failed to fetch receipt data");
      const data = await res.json();
      if (data.length === 0) throw new Error("No data found");
      setReceiptData(data[0]); setShowReceipt(true);
    } catch (e) { console.error(e); }
  };

  const handlePageChange = (page) => { handleSearch(page, rowsPerPage || 10); };
  
  const effectiveRows = rowsPerPage || 10;
  const totalPages = Math.ceil(totalRecords / effectiveRows);
  const formatNumber = (num) => (typeof num === "number" ? num.toFixed(2) : "0.00");

  const renderPagination = () => {
    if (!effectiveRows || totalRecords === 0) return null;
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

    return (
      <div className="pagination-controls">
        <button className="btn-page" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i></button>
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(num => (
            <button key={num} className={`btn-page ${currentPage === num ? "active" : ""}`} onClick={() => handlePageChange(num)}>{num}</button>
        ))}
        <button className="btn-page" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}><i className="bi bi-chevron-right"></i></button>
      </div>
    );
  };

  return (
    <div className="report-summary-container">
      <div className="report-card">
        <div className="report-header">
           <div className="header-left">
             <div className="header-icon"><i className="bi bi-file-earmark-bar-graph"></i></div>
             <div className="header-text"><h5>Bill Summary Report</h5></div>
           </div>
        </div>

        <div className="card-content">
          
          {/* Filters Section */}
          <div className="filters-section">
             <div className="filter-group">
                <label><i className="bi bi-calendar"></i> From Date</label>
                <input type="date" className="filter-input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
             </div>
             <div className="filter-group">
                <label><i className="bi bi-calendar"></i> To Date</label>
                <input type="date" className="filter-input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
             </div>
             <div className="filter-group flex-2">
                <label><i className="bi bi-search"></i> Search Patient</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadSearchOptions}
                  onChange={handleSearchSelect}
                  value={selectedSearch}
                  placeholder="Search by Name or Mobile..."
                  isClearable
                  styles={selectStyles}
                />
             </div>
             <div className="filter-group">
                <label><i className="bi bi-list-ol"></i> Rows</label>
                <input type="number" className="filter-input" value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} />
             </div>
             <div className="filter-group align-end">
                <button className="btn-search" onClick={() => handleSearch(1, rowsPerPage)}>Search</button>
             </div>
          </div>

          <div className="records-info">
              Showing {((currentPage - 1) * effectiveRows) + 1} to {Math.min(currentPage * effectiveRows, totalRecords)} of {totalRecords} entries
          </div>

          {/* Table Section */}
          <div className="table-section">
            <div className="table-responsive">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Mobile</th><th>Bill No</th><th>Date</th>
                    <th className="text-right">Total</th><th className="text-right">Received</th>
                    <th className="text-right">Cash</th><th className="text-right">UPI</th>
                    <th className="text-right">Due</th>
                    
                    {/* ✅ Amount Header */}
                    <th className="text-right">Discount (₹)</th>
                    
                    <th>Authority</th><th>Req. By</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {billDataList.length === 0 ? (
                    <tr><td colSpan="13" className="empty-state"><p>No records found.</p></td></tr>
                  ) : (
                    billDataList.map(({ customer, summary }) => (
                      <tr key={summary.billNo}>
                        <td>{customer?.custName || "-"}</td>
                        <td>{customer?.mobileNo || "-"}</td>
                        <td className="fw-medium text-primary">{summary.billNo}</td>
                        <td>{new Date(summary.billDate).toLocaleDateString()}</td>
                        <td className="text-right fw-bold">₹{formatNumber(summary.total)}</td>
                        <td className="text-right text-success">₹{formatNumber(summary.received)}</td>
                        <td className="text-right">₹{formatNumber(summary.cash)}</td>
                        <td className="text-right">₹{formatNumber(summary.upi)}</td>
                        <td className="text-right fw-bold" style={{color: summary.due > 0 ? '#e74c3c' : '#27ae60'}}>₹{formatNumber(summary.due)}</td>
                        
                        {/* ✅ Display Amount */}
                        <td className="text-right">₹{formatNumber(summary.dis)}</td>
                        
                        <td>{summary.disAuthority}</td>
                        <td>{summary.requestedBy}</td>
                        <td className="text-center">
                           <div className="btn-group-actions">
                             <button className="btn-icon btn-edit" onClick={(e) => handleEditIconClick(e, summary.billNo)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                             <button className="btn-icon btn-delete" onClick={() => handleDeleteSummary(summary.billNo)} title="Delete"><i className="bi bi-trash"></i></button>
                             <button className="btn-icon btn-preview" onClick={() => handlePreviewClick(summary.billNo)} title="View"><i className="bi bi-eye"></i></button>
                           </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && <div className="pagination-section">{renderPagination()}</div>}

        </div>
      </div>

      {editBillNo && (
        <Billpopup
          billNo={editBillNo} 
          onClose={() => setEditBillNo(null)} 
          position={popupPosition}
          // ✅ AUTO OPEN RECEIPT ON UPDATE
          onUpdateSuccess={async () => { 
             await handleSearch(currentPage, rowsPerPage); // Refresh Table
             await handlePreviewClick(editBillNo); // 1. Fetch updated data & Open Receipt
             setEditBillNo(null); // 2. Close Edit Popup
          }}
        />
      )}

      {showReceipt && receiptData && companyDetails && (
        <CustomerBillingReceipt show={showReceipt} receiptData={receiptData} companyDetails={companyDetails} onClose={() => setShowReceipt(false)} onPrint={() => window.print()} />
      )}

      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .report-summary-container { padding: 0; font-family: system-ui, sans-serif; }
        .report-card { background: white; border-radius: 6px; box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06); border: 1px solid #e0e6ed; overflow: visible; }

        /* HEADER */
        .report-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-bottom: 2px solid rgba(0,0,0,0.1); border-radius: 6px 6px 0 0; }
        .header-left { display: flex; align-items: center; gap: 8px; color: white; }
        .header-icon { width: 26px; height: 26px; background: rgba(255, 255, 255, 0.2); border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 13px; font-weight: 600; line-height: 1.2; }

        .card-content { padding: 10px 12px; }

        /* FILTERS */
        .filters-section { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; background: #f8f9fa; padding: 8px 10px; border-radius: 5px; border: 1px solid #e0e6ed; margin-bottom: 10px; }
        .filter-group { display: flex; flex-direction: column; }
        .filter-group.flex-2 { grid-column: span 2; }
        .filter-group label { font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; display: flex; align-items: center; gap: 4px; }
        .filter-group label i { color: var(--theme-color, #0088cc); font-size: 11px; }
        
        .filter-input { height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; transition: none; width: 100%; }
        .filter-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color), transparent 85%); }

        .align-end { justify-content: flex-end; }
        .btn-search { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: opacity 0.2s; margin-top: auto; }
        .btn-search:hover { opacity: 0.9; }

        .records-info { font-size: 11px; color: #64748b; margin-bottom: 8px; font-weight: 500; }

        /* TABLE */
        .table-section { background: white; border-radius: 4px; border: 1px solid #e0e6ed; overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .custom-table thead { background: #f1f5f9; border-bottom: 1px solid #cbd5e0; }
        .custom-table th { color: #2c3e50; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; white-space: nowrap; border-right: 1px solid #e0e6ed; }
        .custom-table th:last-child { border-right: none; }
        .custom-table td { padding: 4px 8px; border-bottom: 1px solid #e0e6ed; border-right: 1px solid #e0e6ed; vertical-align: middle; white-space: nowrap; }
        .custom-table td:last-child { border-right: none; }
        .custom-table tr:hover { background: #f8fafc; }

        /* UTILS */
        .text-right { text-align: right !important; }
        .text-center { text-align: center !important; }
        .fw-bold { font-weight: 700; }
        .fw-medium { font-weight: 600; }
        .text-success { color: #15803d !important; }
        .text-danger { color: #dc2626 !important; }
        .text-primary { color: var(--theme-color, #0088cc) !important; }

        /* BUTTONS */
        .btn-icon { width: 22px; height: 22px; border-radius: 3px; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s; margin: 0 2px; font-size: 11px; }
        .btn-edit { background: #e0f2fe; color: #0284c7; }
        .btn-edit:hover { background: #0284c7; color: white; }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; }
        .btn-preview { background: #fef08a; color: #854d0e; }
        .btn-preview:hover { background: #eab308; color: white; }
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }

        .empty-state { text-align: center; padding: 20px; color: #94a3b8; font-size: 11px; }

        /* PAGINATION */
        .pagination-section { display: flex; justify-content: center; margin-top: 10px; padding-top: 10px; border-top: 1px solid #e0e6ed; }
        .pagination-controls { display: flex; gap: 4px; }
        .btn-page { width: 24px; height: 24px; border: 1px solid #cbd5e0; background: white; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; transition: all 0.2s; color: #2c3e50; }
        .btn-page:hover:not(:disabled) { background: #f1f5f9; border-color: var(--theme-color, #0088cc); color: var(--theme-color, #0088cc); }
        .btn-page.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        .btn-page:disabled { opacity: 0.5; cursor: not-allowed; background: #f8fafc; }

        @media (max-width: 768px) {
          .filters-section { grid-template-columns: 1fr; }
          .filter-group.flex-2 { grid-column: span 1; }
          .custom-table { min-width: 1000px; }
        }
      `}</style>
    </div>
  );
};

export default ReportBillSummary;